/**
 * Header Animation Module
 * Controls the animation sequence for the header
 */

function easeOutCubic(t) {
  return 1 - Math.pow(1 - t, 3);
}

function normDigit(n) {
  return ((Math.round(n) % 10) + 10) % 10;
}

/** Shortest path on 0–9 ring from start to end (inclusive of both ends). */
function shortestDigitPath(start, end) {
  const s = normDigit(start);
  const e = normDigit(end);
  const path = [s];
  if (s === e) return path;
  const forward = (e - s + 10) % 10;
  const backward = (s - e + 10) % 10;
  if (forward <= backward) {
    for (let i = 1; i <= forward; i++) {
      path.push((s + i) % 10);
    }
  } else {
    for (let i = 1; i <= backward; i++) {
      path.push((s - i + 10) % 10);
    }
  }
  return path;
}

// Intro: N 0 3 → N 9 6 — 12 steps each (indices 0–11).
const INTRO_PATH_FIRST = [0, 1, 2, 3, 2, 3, 4, 5, 6, 7, 8, 9];
const INTRO_PATH_LAST = [3, 4, 5, 4, 3, 2, 1, 0, 9, 8, 7, 6];

class HeaderAnimation {
  constructor(options = {}) {
    // Configuration with defaults
    this.config = {
      // Animation speeds
      counterDuration: 1450, // Duration for intro digits + title (shared timeline, ms)
      stepDelay: 300, // Delay between animation steps (ms)
      hoverTickMs: 110, // ms between digit steps while hovering numbers
      returnDurationMs: 650, // ms to ease back to 9 / 6 after hover

      // Selectors
      headerSelector: ".header",
      numbersContainerSelector: ".header__numbers",
      numberFirstSelector: ".header__numbers-number--first",
      numberLastSelector: ".header__numbers-number--last",
      titleSelector: ".header__menu__title",
      homeSelector: "main.home",

      // Override defaults with provided options
      ...options,
    };

    // Animation state
    this.isAnimating = false;
    this.currentStep = 0;
    this.hoverBound = false;
    this.hoverIntervalId = null;
    this.returnRafId = null;

    this.onNumbersEnter = this.onNumbersEnter.bind(this);
    this.onNumbersLeave = this.onNumbersLeave.bind(this);

    // Initialize elements
    this.header = document.querySelector(this.config.headerSelector);
    this.numbersContainer = document.querySelector(
      this.config.numbersContainerSelector
    );
    this.numberFirst = document.querySelector(this.config.numberFirstSelector);
    this.numberLast = document.querySelector(this.config.numberLastSelector);
    this.title = document.querySelector(this.config.titleSelector);
    this.homeMain = document.querySelector(this.config.homeSelector);

    // Initialize
    this.init();
  }

  /**
   * Initialize animation
   */
  init() {
    if (!this.header || !this.numberFirst || !this.numberLast || !this.title) {
      console.error("Header animation elements not found");
      return;
    }

    // Check if we're on mobile
    this.isMobile = window.innerWidth <= 768;

    // Check if we should use prehome mode or quick mode
    this.isPrehomeMode = this.checkIsPrehomeMode();

    if (this.isPrehomeMode) {
      this.setupPrehomeMode();
    } else {
      // In quick mode, disable transitions and add final state classes immediately
      this.header.style.transition = "none";
      this.header.classList.add("header--animate-step3");
      this.header.classList.add("header--animate-step4");
      
      // Re-enable transitions after a frame
      requestAnimationFrame(() => {
        this.header.style.transition = "";
      });
      
      this.setupQuickMode();
    }
  }

  /**
   * Check if we should use prehome mode
   * Only use prehome mode on exactly the root URL with no hash or query params
   */
  checkIsPrehomeMode() {
    // Get the full URL components
    const fullUrl = window.location.href;
    const baseUrl = window.location.origin + "/";

    // We want prehome mode ONLY for the exact URLs:
    // "https://domain.com/" or "https://domain.com"
    const isPrehomeMode =
      fullUrl === baseUrl || fullUrl === baseUrl.slice(0, -1);

    return isPrehomeMode;
  }

  /**
   * Setup prehome mode animation
   */
  setupPrehomeMode() {
    // Set initial values
    this.resetToInitialState();

    // Start animation sequence after a short delay
    setTimeout(() => {
      this.startAnimationSequence();
    }, 580);
  }

  /**
   * Setup quick mode (skip animations)
   */
  setupQuickMode() {
    // Set final values directly without any animation

    // Set numbers to final state
    this.numberFirst.textContent = "9";
    this.numberLast.textContent = "6";
    this.numberFirst.style.color = "#000000";
    this.numberLast.style.color = "#000000";

    // Set title to final state
    this.title.innerHTML = "Numeronovantasei";

    // Disable transitions on title and inner for mobile
    if (this.isMobile) {
      this.title.style.transition = "none";
      const headerInner = document.querySelector(".header__inner");
      if (headerInner) {
        headerInner.style.transition = "none";
      }
      
      // Re-enable after a frame
      requestAnimationFrame(() => {
        this.title.style.transition = "";
        if (headerInner) {
          headerInner.style.transition = "";
        }
      });
    }

    // Set buttons to final state without transition
    const buttons = document.querySelectorAll(".header__menu__button");
    buttons.forEach((button) => {
      // Disable transition temporarily
      button.style.transition = "none";
      button.style.transform = "translateY(0)";

      // Force reflow to apply changes immediately
      void button.offsetWidth;

      // Re-enable transition for future changes
      setTimeout(() => {
        button.style.transition = "";
      }, 10);
    });

    // Show main content immediately in quick mode
    setTimeout(() => {
      this.showMainContent();
    }, 50);

    this.enableNumberHover();
  }

  /**
   * Reset to initial animation state (Step 0)
   */
  resetToInitialState() {
    // Reset numbers (intro ends at N 9 6)
    this.numberFirst.textContent = "0";
    this.numberLast.textContent = "3";
    this.numberFirst.style.color = "#d0d0d0";
    this.numberLast.style.color = "#d0d0d0";

    // Reset title
    this.title.innerHTML = "";

    // Reset buttons (already handled by CSS)
  }

  /**
   * Start the animation sequence
   */
  startAnimationSequence() {
    if (this.isAnimating) return;
    this.isAnimating = true;

    // Track completion of parallel animations
    this.animationsComplete = {
      numbers: false,
      title: false
    };

    // Shared clock so title easing matches digit easing exactly
    this.introAnimStartTime = performance.now();
    this.animateNumbers();
    this.animateTitle();
  }

  /**
   * Check if both parallel animations are complete
   */
  checkParallelAnimationsComplete() {
    if (this.animationsComplete.numbers && this.animationsComplete.title) {
      // Both animations complete, move to step 3
      this.animateHeaderPosition();
    }
  }

  /**
   * Step 1: Animate numbers
   */
  animateNumbers() {
    const pathFirst = INTRO_PATH_FIRST;
    const pathLast = INTRO_PATH_LAST;
    const endFirst = 9;
    const endLast = 6;
    const segFirst = pathFirst.length - 1;
    const segLast = pathLast.length - 1;
    const maxSeg = Math.max(segFirst, segLast, 1);
    const duration = this.config.counterDuration;

    const startTime = this.introAnimStartTime;

    const animate = (currentTime) => {
      const elapsed = currentTime - startTime;
      const linear = Math.min(elapsed / duration, 1);
      const eased = easeOutCubic(linear);

      const iFirst =
        segFirst === 0
          ? 0
          : Math.min(segFirst, Math.round(eased * maxSeg));
      const iLast =
        segLast === 0
          ? 0
          : Math.min(segLast, Math.round(eased * maxSeg));

      const currentFirst = pathFirst[iFirst];
      const currentLast = pathLast[iLast];

      this.numberFirst.textContent = String(currentFirst);
      this.numberLast.textContent = String(currentLast);

      if (currentFirst === endFirst) {
        this.numberFirst.style.color = "#000000";
      }

      if (currentLast === endLast) {
        this.numberLast.style.color = "#000000";
      }

      if (linear < 1) {
        requestAnimationFrame(animate);
      } else {
        this.numberFirst.textContent = String(pathFirst[segFirst]);
        this.numberLast.textContent = String(pathLast[segLast]);
        this.numberFirst.style.color = "#000000";
        this.numberLast.style.color = "#000000";
        this.header.classList.add("header--animate-complete");
        this.animationsComplete.numbers = true;
        this.checkParallelAnimationsComplete();
      }
    };

    requestAnimationFrame(animate);
  }

  /**
   * Step 2: Animate title text (same duration + easeOutCubic as digits)
   */
  animateTitle() {
    const text = "Numeronovantasei";
    const n = text.length;
    const duration = this.config.counterDuration;
    const startTime = this.introAnimStartTime;

    this.title.innerHTML = "";

    const animate = (currentTime) => {
      const elapsed = currentTime - startTime;
      const linear = Math.min(elapsed / duration, 1);
      const eased = easeOutCubic(linear);

      const visibleLen =
        n <= 1
          ? n
          : Math.min(n, 1 + Math.round(eased * (n - 1)));

      this.title.innerHTML = text.substring(0, visibleLen);

      if (linear < 1) {
        requestAnimationFrame(animate);
      } else {
        this.title.innerHTML = text;
        this.animationsComplete.title = true;
        this.checkParallelAnimationsComplete();
      }
    };

    requestAnimationFrame(animate);
  }

  /**
   * Step 3: Move header to bottom
   */
  animateHeaderPosition() {
    this.header.classList.add("header--animate-step3");
    this.currentStep = 3;

    const transitionEnd = () => {
      this.header.removeEventListener("transitionend", transitionEnd);
      this.showMainContent();
      setTimeout(() => {
        this.animateButtons();
      }, 100);
    };

    this.header.addEventListener("transitionend", transitionEnd);
  }

  /**
   * Step 4: Animate buttons
   */
  animateButtons() {
    this.header.classList.add("header--animate-step4");
    this.currentStep = 4;

    setTimeout(() => {
      this.isAnimating = false;
      this.enableNumberHover();
    }, 320);
  }

  cancelHoverTimers() {
    if (this.hoverIntervalId !== null) {
      clearInterval(this.hoverIntervalId);
      this.hoverIntervalId = null;
    }
    if (this.returnRafId !== null) {
      cancelAnimationFrame(this.returnRafId);
      this.returnRafId = null;
    }
  }

  enableNumberHover() {
    if (this.hoverBound || !this.numbersContainer) return;
    this.hoverBound = true;
    this.numbersContainer.addEventListener(
      "mouseenter",
      this.onNumbersEnter
    );
    this.numbersContainer.addEventListener(
      "mouseleave",
      this.onNumbersLeave
    );
  }

  onNumbersEnter() {
    this.cancelHoverTimers();
    const tick = () => {
      const a = normDigit(
        parseInt(this.numberFirst.textContent, 10) + 1
      );
      const b = normDigit(
        parseInt(this.numberLast.textContent, 10) + 1
      );
      this.numberFirst.textContent = String(a);
      this.numberLast.textContent = String(b);
      this.numberFirst.style.color = "#000000";
      this.numberLast.style.color = "#000000";
    };
    tick();
    this.hoverIntervalId = setInterval(
      tick,
      this.config.hoverTickMs
    );
  }

  onNumbersLeave() {
    if (this.hoverIntervalId !== null) {
      clearInterval(this.hoverIntervalId);
      this.hoverIntervalId = null;
    }

    const targetFirst = 9;
    const targetLast = 6;
    const curFirst = normDigit(
      parseInt(this.numberFirst.textContent, 10) || 0
    );
    const curLast = normDigit(
      parseInt(this.numberLast.textContent, 10) || 0
    );

    if (curFirst === targetFirst && curLast === targetLast) {
      return;
    }

    const pathFirst = shortestDigitPath(curFirst, targetFirst);
    const pathLast = shortestDigitPath(curLast, targetLast);
    const segFirst = Math.max(0, pathFirst.length - 1);
    const segLast = Math.max(0, pathLast.length - 1);
    const maxSeg = Math.max(segFirst, segLast, 1);

    const startTime = performance.now();
    const duration = this.config.returnDurationMs;

    const step = (now) => {
      const linear = Math.min((now - startTime) / duration, 1);
      const eased = easeOutCubic(linear);

      const iFirst =
        segFirst === 0
          ? 0
          : Math.min(
              segFirst,
              Math.round(eased * segFirst)
            );
      const iLast =
        segLast === 0
          ? 0
          : Math.min(segLast, Math.round(eased * segLast));

      this.numberFirst.textContent = String(pathFirst[iFirst]);
      this.numberLast.textContent = String(pathLast[iLast]);
      this.numberFirst.style.color = "#000000";
      this.numberLast.style.color = "#000000";

      if (linear < 1) {
        this.returnRafId = requestAnimationFrame(step);
      } else {
        this.numberFirst.textContent = String(targetFirst);
        this.numberLast.textContent = String(targetLast);
        this.returnRafId = null;
      }
    };

    this.returnRafId = requestAnimationFrame(step);
  }

  /**
   * Complete animation step and move to next
   */
  completeStep(step) {
    this.currentStep = step;
    this.header.classList.add(`header--animate-step${step}`);

    // Delay before starting next step
    // Use a shorter delay for step 3 to step 4
    const delay = step === 3 ? 150 : this.config.stepDelay;

    setTimeout(() => {
      switch (step) {
        case 1:
          this.animateTitle();
          break;
        case 2:
          this.animateHeaderPosition();
          break;
        case 3:
          this.animateButtons();
          break;
        case 4:
          // Animation sequence complete
          this.isAnimating = false;
          // Show main content after all animations complete
          this.showMainContent();
          break;
      }
    }, delay);
  }

  /**
   * Show main content by setting opacity to 1
   */
  showMainContent() {
    if (this.homeMain) {
      this.homeMain.style.opacity = "1";
    }
  }
}

// Create and export singleton instance
const headerAnimation = new HeaderAnimation();
export default headerAnimation;
